import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuth } from './AuthContext';
import { configuracionAPI } from '../services/api';
import {
  setZonaHorariaSistema,
  setFormatoFechaSistema,
  fechaHoySistema,
  formatFechaSistema,
  formatFechaHoraSistema,
  relojSistema,
  etiquetaZona,
  etiquetaFormatoFecha,
  getZonasDisponibles,
  getFormatosFechaDisponibles,
  FORMATO_FECHA_DEFAULT,
} from '../utils/fechasSistema';

const ZonaHorariaContext = createContext(null);

export function ZonaHorariaProvider({ children }) {
  const { user } = useAuth();
  const [zonaHoraria, setZonaHoraria] = useState('America/Argentina/Buenos_Aires');
  const [formatoFecha, setFormatoFecha] = useState(FORMATO_FECHA_DEFAULT);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setLoading(false);
      return;
    }
    try {
      const res = await configuracionAPI.getSistema();
      if (res.success && res.data) {
        if (res.data.zona_horaria) {
          setZonaHoraria(res.data.zona_horaria);
          setZonaHorariaSistema(res.data.zona_horaria);
        }
        const fmt = res.data.formato_fecha || FORMATO_FECHA_DEFAULT;
        setFormatoFecha(fmt);
        setFormatoFechaSistema(fmt);
      }
    } catch {
      /* mantener default */
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const value = useMemo(
    () => ({
      zonaHoraria,
      formatoFecha,
      loading,
      refresh,
      zonasDisponibles: getZonasDisponibles(),
      formatosFechaDisponibles: getFormatosFechaDisponibles(),
      etiquetaZona: () => etiquetaZona(zonaHoraria),
      etiquetaFormatoFecha: () => etiquetaFormatoFecha(formatoFecha),
      fechaHoy: () => fechaHoySistema(zonaHoraria),
      formatFecha: (f, opts) => formatFechaSistema(f, { ...opts, zona: zonaHoraria, formato: formatoFecha }),
      formatFechaHora: (f) => formatFechaHoraSistema(f, zonaHoraria, formatoFecha),
      reloj: () => relojSistema(zonaHoraria),
    }),
    [zonaHoraria, formatoFecha, loading, refresh]
  );

  return (
    <ZonaHorariaContext.Provider value={value}>{children}</ZonaHorariaContext.Provider>
  );
}

export function useZonaHoraria() {
  const ctx = useContext(ZonaHorariaContext);
  if (!ctx) {
    throw new Error('useZonaHoraria debe usarse dentro de ZonaHorariaProvider');
  }
  return ctx;
}

/** Hook seguro fuera del provider (p. ej. login): usa zona y formato por defecto. */
export function useZonaHorariaOpcional() {
  const ctx = useContext(ZonaHorariaContext);
  if (!ctx) {
    return {
      zonaHoraria: 'America/Argentina/Buenos_Aires',
      formatoFecha: FORMATO_FECHA_DEFAULT,
      formatFecha: (f, opts) => formatFechaSistema(f, opts),
      formatFechaHora: (f) => formatFechaHoraSistema(f),
      fechaHoy: () => fechaHoySistema(),
      reloj: () => relojSistema(),
    };
  }
  return ctx;
}
